import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Button, Checkbox, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { useStopCoding, useStopCodingDispatch } from "../OI/redux/ReduxContext";
import { StopCause } from "../../Types/StopCause";
import { createStripedPattern } from "../../utils/chartPatterns";
import { useDarkMode } from "../../hooks/useDarkMode";
import { getSecoCategoryColor, SECO_BLUE, SECO_BLUE_TINT_20 } from "./stopReasonColors";
import {
    filterStopCausesForCascade,
    getDirectStopReasonNodePath,
    getStopReasonBreadcrumbs,
    getStopReasonNodeLabel,
    getStopReasonPath,
    getStopReasonPathLabel,
    getStopReasonSelectionPath,
    getStopReasonPathSegments,
    isDirectStopReasonNodePath,
    isStopReasonInCascadePath,
    isValidStopCodingDate
} from "./stopCodingCascadeUtils";

interface StopReasonDatum {
    id: string;
    reason: string;
    fullReason: string;
    axisLabel: string;
    chartPath: string;
    selectionPath: string;
    visibilityKey: string;
    value: number;
    isOklassificerat: boolean;
    hasChildren: boolean;
}

type StopReasonLevelMap = Record<string, StopReasonDatum[]>;
const ROOT_REASON_GROUP_ID = '__root__';
const FLAT_REASON_GROUP_ID = '__flat__';

const calculateDurationHours = (stops: StopCause[]): number => {
    return stops.reduce((total, stop) => {

        const startDate = new Date(stop.StartTime);
        const endDate = new Date(stop.EndTime);
        const duration = stop.Duration.minutes

        if (!isValidStopCodingDate(startDate) || !isValidStopCodingDate(endDate) || endDate.getFullYear() > 3000) {
            return total + (duration ? duration : 0);
        }

        return total + (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    }, 0);
};

const formatDurationHM = (hours: number): string => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    return `${wholeHours}h ${minutes}m`;
};

const formatDurationAxisHours = (hours: number): string => {
    if (hours <= 0) {
        return '0h';
    }

    if (hours >= 1000) {
        const scaledHours = hours / 1000;
        return `${scaledHours.toFixed(1).replace(/\.0$/, '')}K`;
    }

    return `${Math.ceil(hours)}h`;
};

const formatCompactCountAxisValue = (value: number): string => {
    const absoluteValue = Math.abs(value);

    if (absoluteValue < 1000) {
        return `${value}`;
    }

    const scaledValue = value / 1000;
    return `${scaledValue.toFixed(1).replace(/\.0$/, '')}K`;
};

const getDurationAxisScale = (maxHours: number): { interval: number; max: number } => {
    const paddedMax = maxHours <= 0 ? 1 : maxHours * 1.1;
    const targetSplitCount = 4;
    const minimumInterval = Math.max(1, Math.ceil(paddedMax / targetSplitCount));
    const niceIntervals = [1, 2, 3, 4, 6, 8, 12, 24];
    const interval = niceIntervals.find((candidate) => candidate >= minimumInterval) ?? minimumInterval;

    return {
        interval,
        max: Math.max(interval, Math.ceil(paddedMax / interval) * interval)
    };
};

const wrapCategoryLabel = (value: string, maxLineLength: number): string => {
    const normalizedValue = value.trim();

    if (normalizedValue.length <= maxLineLength) {
        return normalizedValue;
    }

    const segments = normalizedValue
        .replace(/\//g, '/ ')
        .split(/\s+/)
        .filter(Boolean);

    const lines: string[] = [];
    let currentLine = '';

    segments.forEach((segment) => {
        const nextLine = currentLine ? `${currentLine} ${segment}` : segment;

        if (nextLine.length <= maxLineLength) {
            currentLine = nextLine;
            return;
        }

        if (currentLine) {
            lines.push(currentLine);
        }

        if (segment.length <= maxLineLength) {
            currentLine = segment;
            return;
        }

        const chunkedSegment = segment.match(new RegExp(`.{1,${maxLineLength}}`, 'g')) ?? [segment];
        lines.push(...chunkedSegment.slice(0, -1));
        currentLine = chunkedSegment[chunkedSegment.length - 1];
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    return lines.join('\n');
};

const getValuePillLabel = (chartMode: 'count' | 'duration') => ({
    show: true,
    position: 'top' as const,
    color: '#ffffff',
    fontWeight: 'bold' as const,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: [4, 8] as [number, number],
    borderRadius: 4,
    formatter: (params: any) => {
        if (chartMode === 'duration') {
            return formatDurationHM(Number(params.value));
        }

        return params.value;
    }
});

const getVisibleBarCount = (viewportWidth: number): number => {
    if (viewportWidth < 640) {
        return 4;
    }

    if (viewportWidth < 1024) {
        return 7;
    }

    return 10;
};

const getBarWidth = (viewportWidth: number): number => {
    if (viewportWidth < 640) {
        return 28;
    }

    if (viewportWidth < 1024) {
        return 36;
    }

    return 44;
};

const getLastStopReasonSegment = (value: string): string => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return '';
    }

    const segments = trimmedValue
        .split('/')
        .map((segment) => segment.trim())
        .filter(Boolean);

    return segments[segments.length - 1] ?? trimmedValue;
};

export default function StopCodingCascade() {
    const { state } = useStopCoding();
    const { allStopCauses, chartMode, startDate, endDate, cascadeReasonVisibility, selectedCascadeReason } = state;
    const stopCodingDispatch = useStopCodingDispatch();
    const stripedPattern = useMemo(() => createStripedPattern(SECO_BLUE), []);
    const chartRef = useRef<any>(null);
    const triggerContainerRef = useRef<HTMLDivElement>(null);
    const isDarkMode = useDarkMode();
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [popoverWidth, setPopoverWidth] = useState<number>();
    const [isGroupingEnabled, setIsGroupingEnabled] = useState(false);
    const isMobileView = viewportWidth < 640;

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = chartRef.current.getEchartsInstance();
        chartInstance?.resize();
    }, [isDarkMode]);

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            setPopoverWidth(triggerContainerRef.current?.offsetWidth);
            chartRef.current?.getEchartsInstance?.()?.resize?.();
        };

        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const filteredStopCauses = useMemo(() => {
        return filterStopCausesForCascade(allStopCauses, startDate, endDate);
    }, [allStopCauses, endDate, startDate]);

    const allReasonPaths = useMemo(() => {
        return filteredStopCauses
            .map((stop) => getStopReasonPath(stop.MachineStopReason))
            .filter(Boolean);
    }, [filteredStopCauses]);

    const stopReasonLevelMap = useMemo<StopReasonLevelMap>(() => {
        const exactReasonMap = new Map<string, StopCause[]>();
        const childReasonMap = new Map<string, Map<string, StopCause[]>>();

        filteredStopCauses.forEach((stop) => {
            const fullPath = getStopReasonPath(stop.MachineStopReason);

            if (!fullPath) {
                return;
            }

            const existingExactStops = exactReasonMap.get(fullPath);

            if (existingExactStops) {
                existingExactStops.push(stop);
            } else {
                exactReasonMap.set(fullPath, [stop]);
            }

            const pathSegments = getStopReasonPathSegments(fullPath);

            pathSegments.forEach((_, index) => {
                const parentPath = pathSegments.slice(0, index).join('/');
                const childPath = pathSegments.slice(0, index + 1).join('/');
                const parentGroups = childReasonMap.get(parentPath) ?? new Map<string, StopCause[]>();
                const childStops = parentGroups.get(childPath);

                if (childStops) {
                    childStops.push(stop);
                } else {
                    parentGroups.set(childPath, [stop]);
                }

                childReasonMap.set(parentPath, parentGroups);
            });
        });

        const allParentPaths = new Set<string>(['', ...Array.from(childReasonMap.keys()), ...Array.from(exactReasonMap.keys())]);
        const nextLevelMap: StopReasonLevelMap = {};

        allParentPaths.forEach((parentPath) => {
            const levelItems: StopReasonDatum[] = [];
            const exactStops = exactReasonMap.get(parentPath);
            const childGroups = childReasonMap.get(parentPath);
            const directNodePath = getDirectStopReasonNodePath(parentPath);

            if (parentPath && exactStops?.length) {
                levelItems.push({
                    id: directNodePath,
                    reason: getStopReasonNodeLabel(parentPath),
                    fullReason: getStopReasonSelectionPath(parentPath),
                    axisLabel: getStopReasonNodeLabel(parentPath),
                    chartPath: directNodePath,
                    selectionPath: directNodePath,
                    visibilityKey: directNodePath,
                    value: chartMode === 'duration' ? calculateDurationHours(exactStops) : exactStops.length,
                    isOklassificerat: parentPath.toLowerCase() === 'oklassificerat',
                    hasChildren: true
                });

                nextLevelMap[directNodePath] = [{
                    id: directNodePath,
                    reason: getStopReasonNodeLabel(parentPath),
                    fullReason: getStopReasonSelectionPath(parentPath),
                    axisLabel: getStopReasonNodeLabel(parentPath),
                    chartPath: directNodePath,
                    selectionPath: directNodePath,
                    visibilityKey: directNodePath,
                    value: chartMode === 'duration' ? calculateDurationHours(exactStops) : exactStops.length,
                    isOklassificerat: parentPath.toLowerCase() === 'oklassificerat',
                    hasChildren: false
                }];
            }

            Array.from(childGroups?.entries() ?? [])
                .map(([fullPath, stops]) => {
                    const label = getStopReasonPathLabel(fullPath, parentPath);

                    return {
                        id: fullPath,
                        reason: label,
                        fullReason: getStopReasonSelectionPath(fullPath),
                        axisLabel: label,
                        chartPath: fullPath,
                        selectionPath: fullPath,
                        visibilityKey: fullPath,
                        value: chartMode === 'duration' ? calculateDurationHours(stops) : stops.length,
                        isOklassificerat: label.toLowerCase() === 'oklassificerat',
                        hasChildren: (childReasonMap.get(fullPath)?.size ?? 0) > 0
                    };
                })
                .sort((left, right) => {
                    return right.value - left.value;
                })
                .forEach((item) => levelItems.push(item));

            nextLevelMap[parentPath] = levelItems;
        });

        return nextLevelMap;
    }, [chartMode, filteredStopCauses]);

    const flatStopReasonData = useMemo<StopReasonDatum[]>(() => {
        const exactReasonMap = new Map<string, StopCause[]>();

        filteredStopCauses.forEach((stop) => {
            const fullPath = getStopReasonPath(stop.MachineStopReason);

            if (!fullPath) {
                return;
            }

            const existingStops = exactReasonMap.get(fullPath);

            if (existingStops) {
                existingStops.push(stop);
                return;
            }

            exactReasonMap.set(fullPath, [stop]);
        });

        return Array.from(exactReasonMap.entries())
            .map(([fullPath, stops]) => {
                const fullReason = getStopReasonSelectionPath(fullPath);

                return {
                    id: fullPath,
                    reason: fullReason,
                    fullReason,
                    axisLabel: getLastStopReasonSegment(fullReason),
                    chartPath: fullPath,
                    selectionPath: fullPath,
                    visibilityKey: fullPath,
                    value: chartMode === 'duration' ? calculateDurationHours(stops) : stops.length,
                    isOklassificerat: fullReason.toLowerCase() === 'oklassificerat',
                    hasChildren: false
                };
            })
            .sort((left, right) => right.value - left.value);
    }, [chartMode, filteredStopCauses]);

    const allStopReasonItems = useMemo(() => {
        return Object.values(stopReasonLevelMap).flat();
    }, [stopReasonLevelMap]);

    useEffect(() => {
        const missingVisibilityEntries = allStopReasonItems.filter((item) => cascadeReasonVisibility[item.visibilityKey] === undefined);

        if (missingVisibilityEntries.length > 0) {
            stopCodingDispatch({
                type: 'SET_FIELD',
                payload: {
                    field: 'cascadeReasonVisibility',
                    value: missingVisibilityEntries.reduce<Record<string, boolean>>((accumulator, item) => {
                        accumulator[item.visibilityKey] = true;
                        return accumulator;
                    }, { ...cascadeReasonVisibility })
                }
            });
        }
    }, [allStopReasonItems, cascadeReasonVisibility, stopCodingDispatch]);

    const stopReasonData = useMemo<StopReasonDatum[]>(() => {
        if (!isGroupingEnabled) {
            return flatStopReasonData;
        }

        return stopReasonLevelMap[selectedCascadeReason] ?? stopReasonLevelMap[''] ?? [];
    }, [flatStopReasonData, isGroupingEnabled, selectedCascadeReason, stopReasonLevelMap]);

    const visibleStopReasonData = useMemo(() => {
        return stopReasonData.filter((item) => cascadeReasonVisibility[item.visibilityKey] !== false);
    }, [cascadeReasonVisibility, stopReasonData]);

    useEffect(() => {
        if (!selectedCascadeReason) {
            return;
        }

        const hasSelectedReason = allReasonPaths.some((reasonPath) => isStopReasonInCascadePath(reasonPath, selectedCascadeReason));

        if (!hasSelectedReason) {
            stopCodingDispatch({
                type: 'SET_FIELD',
                payload: { field: 'selectedCascadeReason', value: '' }
            });
        }
    }, [allReasonPaths, selectedCascadeReason, stopCodingDispatch]);

    const visibleBarCount = useMemo(() => getVisibleBarCount(viewportWidth), [viewportWidth]);
    const chartHeight = isMobileView ? 200 : 260;

    const getChartGroupId = (path: string) => path || ROOT_REASON_GROUP_ID;

    const chartOptionsByPath = useMemo(() => {
        const axisUiColor = isDarkMode ? '#555' : '#ccc';
        const maxLineLength = isMobileView ? 10 : 14;
        const nextOptions: Record<string, any> = {};

        if (!isGroupingEnabled) {
            const categories = flatStopReasonData.map((item) => item.id);
            const shouldShowZoom = categories.length > visibleBarCount;
            const zoomWindowSize = Math.min(categories.length, visibleBarCount);
            const zoomEndValue = Math.max(0, zoomWindowSize - 1);
            const lockedValueSpan = Math.max(0, zoomWindowSize - 1);
            const maxSeriesValue = flatStopReasonData.reduce((highestValue, item) => Math.max(highestValue, item.value), 0);
            const durationAxisScale = getDurationAxisScale(maxSeriesValue);
            const yAxisMax = chartMode === 'duration'
                ? durationAxisScale.max
                : maxSeriesValue <= 0 ? 1 : Math.ceil(maxSeriesValue * 1.1);
            const axisLabelLookup = flatStopReasonData.reduce<Record<string, string>>((accumulator, item) => {
                accumulator[item.id] = item.axisLabel;
                return accumulator;
            }, {});

            nextOptions[''] = {
                id: FLAT_REASON_GROUP_ID,
                animationDurationUpdate: 400,
                animationEasingUpdate: 'cubicOut',
                tooltip: {
                    trigger: 'item',
                    axisPointer: {
                        type: 'shadow',
                        animation: false
                    },
                    formatter: (params: any) => {
                        const item = flatStopReasonData[params.dataIndex];
                        const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;
                        const value = chartMode === 'duration' ? formatDurationHM(Number(rawValue)) : rawValue;
                        return `${params.marker} ${item?.fullReason ?? params.name}: ${value}`;
                    },
                },
                grid: {
                    left: 40,
                    right: 20,
                    top: 28,
                    bottom: 68,
                    containLabel: false
                },
                dataZoom: shouldShowZoom ? [
                    {
                        type: 'inside',
                        xAxisIndex: 0,
                        startValue: 0,
                        endValue: zoomEndValue,
                        filterMode: 'none',
                        zoomLock: true,
                        minValueSpan: lockedValueSpan,
                        maxValueSpan: lockedValueSpan,
                        zoomOnMouseWheel: false,
                        moveOnMouseMove: true,
                        moveOnMouseWheel: true
                    },
                    {
                        type: 'slider',
                        xAxisIndex: 0,
                        startValue: 0,
                        endValue: zoomEndValue,
                        filterMode: 'none',
                        zoomLock: true,
                        minValueSpan: lockedValueSpan,
                        maxValueSpan: lockedValueSpan,
                        height: 18,
                        bottom: 0,
                        fillerColor: 'rgba(0, 108, 234, 0.2)',
                        borderColor: isDarkMode ? '#555' : '#ccc',
                        handleStyle: {
                            color: SECO_BLUE
                        },
                        moveHandleStyle: {
                            color: SECO_BLUE
                        },
                        textStyle: {
                            color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                        },
                        showDetail: false,
                        showDataShadow: false,
                        brushSelect: false
                    }
                ] : [],
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        fontSize: isMobileView ? 11 : 12,
                        color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                        interval: 0,
                        lineHeight: 14,
                        formatter: (value: string) => wrapCategoryLabel(axisLabelLookup[value] ?? value, maxLineLength),
                        margin: isMobileView ? 10 : 14
                    },
                    axisLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    axisTick: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    boundaryGap: true
                },
                yAxis: {
                    type: 'value',
                    min: 0,
                    max: yAxisMax,
                    interval: chartMode === 'duration' ? durationAxisScale.interval : undefined,
                    axisLabel: {
                        fontSize: 12,
                        color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                        formatter: chartMode === 'duration'
                            ? (value: number) => formatDurationAxisHours(value)
                            : (value: number) => formatCompactCountAxisValue(value)
                    },
                    axisLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    axisTick: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    splitLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    }
                },
                series: [{
                    name: chartMode === 'duration' ? 'Duration' : 'Stops',
                    type: 'bar',
                    dimensions: ['x', 'y', 'groupId', 'childGroupId'],
                    encode: {
                        x: 'x',
                        y: 'y',
                        itemGroupId: 'groupId',
                        itemChildGroupId: 'childGroupId'
                    },
                    data: flatStopReasonData.map((item) => [
                        item.id,
                        item.value,
                        getChartGroupId(''),
                        getChartGroupId(getDirectStopReasonNodePath(item.chartPath))
                    ]),
                    barWidth: getBarWidth(viewportWidth),
                    barMaxWidth: getBarWidth(viewportWidth),
                    barMinWidth: getBarWidth(viewportWidth),
                    barMinHeight: 3,
                    itemStyle: {
                        color: (params: any) => {
                            const item = flatStopReasonData[params.dataIndex];

                            if (!item) {
                                return getSecoCategoryColor(params.dataIndex);
                            }

                            return item.isOklassificerat
                                ? {
                                    type: 'pattern',
                                    image: stripedPattern,
                                    repeat: 'repeat'
                                }
                                : getSecoCategoryColor(params.dataIndex);
                        }
                    },
                    label: {
                        ...getValuePillLabel(chartMode),
                        formatter: (params: any) => {
                            const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;

                            if (chartMode === 'duration') {
                                return formatDurationHM(Number(rawValue));
                            }

                            return rawValue;
                        }
                    },
                    emphasis: {
                        disabled: true
                    },
                    universalTransition: {
                        enabled: true,
                        divideShape: 'clone'
                    }
                }]
            };

            flatStopReasonData.forEach((item) => {
                const directPath = getDirectStopReasonNodePath(item.chartPath);

                nextOptions[directPath] = {
                    id: getChartGroupId(directPath),
                    animationDurationUpdate: 400,
                    animationEasingUpdate: 'cubicOut',
                    tooltip: {
                        trigger: 'item',
                        axisPointer: {
                            type: 'shadow',
                            animation: false
                        },
                        formatter: (params: any) => {
                            const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;
                            const value = chartMode === 'duration' ? formatDurationHM(Number(rawValue)) : rawValue;
                            return `${params.marker} ${item.fullReason}: ${value}`;
                        },
                    },
                    grid: {
                        left: 40,
                        right: 20,
                        top: 28,
                        bottom: 68,
                        containLabel: false
                    },
                    xAxis: {
                        type: 'category',
                        data: [item.id],
                        axisLabel: {
                            fontSize: isMobileView ? 11 : 12,
                            color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                            interval: 0,
                            lineHeight: 14,
                            formatter: () => wrapCategoryLabel(item.axisLabel, maxLineLength),
                            margin: isMobileView ? 10 : 14
                        },
                        axisLine: {
                            lineStyle: {
                                color: axisUiColor
                            }
                        },
                        axisTick: {
                            lineStyle: {
                                color: axisUiColor
                            }
                        },
                        boundaryGap: true
                    },
                    yAxis: {
                        type: 'value',
                        min: 0,
                        max: yAxisMax,
                        interval: chartMode === 'duration' ? durationAxisScale.interval : undefined,
                        axisLabel: {
                            fontSize: 12,
                            color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                            formatter: chartMode === 'duration'
                                ? (value: number) => formatDurationAxisHours(value)
                                : (value: number) => formatCompactCountAxisValue(value)
                        },
                        axisLine: {
                            lineStyle: {
                                color: axisUiColor
                            }
                        },
                        axisTick: {
                            lineStyle: {
                                color: axisUiColor
                            }
                        },
                        splitLine: {
                            lineStyle: {
                                color: axisUiColor
                            }
                        }
                    },
                    series: [{
                        name: chartMode === 'duration' ? 'Duration' : 'Stops',
                        type: 'bar',
                        dimensions: ['x', 'y', 'groupId', 'childGroupId'],
                        encode: {
                            x: 'x',
                            y: 'y',
                            itemGroupId: 'groupId',
                            itemChildGroupId: 'childGroupId'
                        },
                        data: [[
                            item.id,
                            item.value,
                            getChartGroupId(directPath),
                            ROOT_REASON_GROUP_ID
                        ]],
                        barWidth: getBarWidth(viewportWidth),
                        barMaxWidth: getBarWidth(viewportWidth),
                        barMinWidth: getBarWidth(viewportWidth),
                        barMinHeight: 3,
                        itemStyle: {
                            color: item.isOklassificerat
                                ? {
                                    type: 'pattern',
                                    image: stripedPattern,
                                    repeat: 'repeat'
                                }
                                : getSecoCategoryColor(flatStopReasonData.findIndex((datum) => datum.chartPath === item.chartPath))
                        },
                        label: {
                            ...getValuePillLabel(chartMode),
                            formatter: (params: any) => {
                                const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;

                                if (chartMode === 'duration') {
                                    return formatDurationHM(Number(rawValue));
                                }

                                return rawValue;
                            }
                        },
                        emphasis: {
                            disabled: true
                        },
                        universalTransition: {
                            enabled: true,
                            divideShape: 'clone'
                        }
                    }]
                };
            });

            return nextOptions;
        }

        Object.entries(stopReasonLevelMap).forEach(([parentPath, levelData]) => {
            const visibleLevelData = levelData.filter((item) => cascadeReasonVisibility[item.visibilityKey] !== false);
            const categories = visibleLevelData.map((item) => item.id);
            const axisLabelLookup = visibleLevelData.reduce<Record<string, string>>((accumulator, item) => {
                accumulator[item.id] = item.axisLabel;
                return accumulator;
            }, {});
            const shouldShowZoom = categories.length > visibleBarCount;
            const zoomWindowSize = Math.min(categories.length, visibleBarCount);
            const zoomEndValue = Math.max(0, zoomWindowSize - 1);
            const lockedValueSpan = Math.max(0, zoomWindowSize - 1);
            const maxSeriesValue = visibleLevelData.reduce((highestValue, item) => Math.max(highestValue, item.value), 0);
            const durationAxisScale = getDurationAxisScale(maxSeriesValue);
            const yAxisMax = chartMode === 'duration'
                ? durationAxisScale.max
                : maxSeriesValue <= 0 ? 1 : Math.ceil(maxSeriesValue * 1.1);

            nextOptions[parentPath] = {
                id: getChartGroupId(parentPath),
                animationDurationUpdate: 400,
                animationEasingUpdate: 'cubicOut',
                tooltip: {
                    trigger: 'item',
                    axisPointer: {
                        type: 'shadow',
                        animation: false
                    },
                    formatter: (params: any) => {
                        const item = visibleLevelData[params.dataIndex];
                        const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;
                        const value = chartMode === 'duration' ? formatDurationHM(Number(rawValue)) : rawValue;
                        return `${params.marker} ${item?.fullReason ?? params.name}: ${value}`;
                    },
                },
                grid: {
                    left: 40,
                    right: 20,
                    top: 28,
                    bottom: 68,
                    containLabel: false
                },
                dataZoom: shouldShowZoom ? [
                    {
                        type: 'inside',
                        xAxisIndex: 0,
                        startValue: 0,
                        endValue: zoomEndValue,
                        filterMode: 'none',
                        zoomLock: true,
                        minValueSpan: lockedValueSpan,
                        maxValueSpan: lockedValueSpan,
                        zoomOnMouseWheel: false,
                        moveOnMouseMove: true,
                        moveOnMouseWheel: true
                    },
                    {
                        type: 'slider',
                        xAxisIndex: 0,
                        startValue: 0,
                        endValue: zoomEndValue,
                        filterMode: 'none',
                        zoomLock: true,
                        minValueSpan: lockedValueSpan,
                        maxValueSpan: lockedValueSpan,
                        height: 18,
                        bottom: 0,
                        fillerColor: 'rgba(0, 108, 234, 0.2)',
                        borderColor: isDarkMode ? '#555' : '#ccc',
                        handleStyle: {
                            color: SECO_BLUE
                        },
                        moveHandleStyle: {
                            color: SECO_BLUE
                        },
                        textStyle: {
                            color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                        },
                        showDetail: false,
                        showDataShadow: false,
                        brushSelect: false
                    }
                ] : [],
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        fontSize: isMobileView ? 11 : 12,
                        color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                        interval: 0,
                        lineHeight: 14,
                        formatter: (value: string) => wrapCategoryLabel(axisLabelLookup[value] ?? value, maxLineLength),
                        margin: isMobileView ? 10 : 14
                    },
                    axisLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    axisTick: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    boundaryGap: true
                },
                yAxis: {
                    type: 'value',
                    min: 0,
                    max: yAxisMax,
                    interval: chartMode === 'duration' ? durationAxisScale.interval : undefined,
                    axisLabel: {
                        fontSize: 12,
                        color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                        formatter: chartMode === 'duration'
                            ? (value: number) => formatDurationAxisHours(value)
                            : (value: number) => formatCompactCountAxisValue(value)
                    },
                    axisLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    axisTick: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    },
                    splitLine: {
                        lineStyle: {
                            color: axisUiColor
                        }
                    }
                },
                series: [{
                    name: chartMode === 'duration' ? 'Duration' : 'Stops',
                    type: 'bar',
                    dimensions: ['x', 'y', 'groupId', 'childGroupId'],
                    encode: {
                        x: 'x',
                        y: 'y',
                        itemGroupId: 'groupId',
                        itemChildGroupId: 'childGroupId'
                    },
                    data: visibleLevelData.map((item) => [
                        item.id,
                        item.value,
                        getChartGroupId(parentPath),
                        getChartGroupId(item.chartPath)
                    ]),
                    barWidth: getBarWidth(viewportWidth),
                    barMaxWidth: getBarWidth(viewportWidth),
                    barMinWidth: getBarWidth(viewportWidth),
                    barMinHeight: 3,
                    itemStyle: {
                        color: (params: any) => {
                            const item = visibleLevelData[params.dataIndex];

                            if (!item) {
                                return getSecoCategoryColor(params.dataIndex);
                            }

                            return item.isOklassificerat
                                ? {
                                    type: 'pattern',
                                    image: stripedPattern,
                                    repeat: 'repeat'
                                }
                                : getSecoCategoryColor(params.dataIndex);
                        }
                    },
                    label: {
                        ...getValuePillLabel(chartMode),
                        formatter: (params: any) => {
                            const rawValue = Array.isArray(params.value) ? params.value[1] : params.value;

                            if (chartMode === 'duration') {
                                return formatDurationHM(Number(rawValue));
                            }

                            return rawValue;
                        }
                    },
                    emphasis: {
                        disabled: true
                    },
                    universalTransition: {
                        enabled: true,
                        divideShape: 'clone'
                    }
                }]
            };
        });

        if (!nextOptions['']) {
            nextOptions[''] = {
                id: ROOT_REASON_GROUP_ID,
                xAxis: { type: 'category' },
                yAxis: { type: 'value' },
                series: []
            };
        }

        return nextOptions;
    }, [cascadeReasonVisibility, chartMode, flatStopReasonData, isDarkMode, isGroupingEnabled, isMobileView, stopReasonLevelMap, stripedPattern, viewportWidth, visibleBarCount]);

    useEffect(() => {
        const nextPath = chartOptionsByPath[selectedCascadeReason] ? selectedCascadeReason : '';
        const chart = chartRef.current?.getEchartsInstance?.();


        if (!chart) {
            return;
        }

        chart.setOption(chartOptionsByPath[nextPath], false);
    }, [chartOptionsByPath, selectedCascadeReason]);

    const setAllReasonsSelected = (nextValue: boolean) => {
        stopCodingDispatch({
            type: 'SET_FIELD',
            payload: {
                field: 'cascadeReasonVisibility',
                value: stopReasonData.reduce<Record<string, boolean>>((accumulator, item) => {
                    accumulator[item.visibilityKey] = nextValue;
                    return accumulator;
                }, { ...cascadeReasonVisibility })
            }
        });
    };

    const toggleReason = (reason: string, nextChecked: boolean) => {
        stopCodingDispatch({
            type: 'SET_FIELD',
            payload: {
                field: 'cascadeReasonVisibility',
                value: {
                    ...cascadeReasonVisibility,
                    [reason]: nextChecked
                }
            }
        });
    };

    const setSelectedCascadeReason = useCallback((nextPath: string) => {
        stopCodingDispatch({
            type: 'SET_FIELD',
            payload: { field: 'selectedCascadeReason', value: nextPath }
        });
    }, [stopCodingDispatch]);

    const chartEvents = useMemo(() => ({
        click: (params: { value?: unknown[] }) => {
            const nextPath = Array.isArray(params.value) ? params.value[3] : undefined;

            if (typeof nextPath !== 'string') {
                return;
            }

            setSelectedCascadeReason(nextPath === ROOT_REASON_GROUP_ID ? '' : nextPath);
        }
    }), [setSelectedCascadeReason]);

    const isUngroupedDirectSelection = !isGroupingEnabled && isDirectStopReasonNodePath(selectedCascadeReason);
    const breadcrumbs = isUngroupedDirectSelection
        ? [selectedCascadeReason]
        : getStopReasonBreadcrumbs(selectedCascadeReason);
    const selectedBreadcrumbPath = isUngroupedDirectSelection
        ? selectedCascadeReason
        : getStopReasonSelectionPath(selectedCascadeReason);
    const visibleCategoryCount = visibleStopReasonData.length;
    const totalCategoryCount = stopReasonData.length;
    const categoryButtonLabel = totalCategoryCount === 0
        ? isGroupingEnabled ? 'Select categories' : 'Select reasons'
        : `${isGroupingEnabled ? 'Select categories' : 'Select reasons'} (${visibleCategoryCount}/${totalCategoryCount})`;

    return (
        <div className="flex w-full flex-col -mt-2">


            <div className="border-8 border-white dark:border-primary-dark">

                <div className="p-4">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 px-5 text-sm text-slate-600 dark:text-slate-300">
                        <div className="flex flex-wrap items-center gap-3">
                            {selectedCascadeReason && (
                                <div className="flex flex-wrap items-center">
                                    <span>
                                        <button
                                            type="button"
                                            className={selectedCascadeReason
                                                ? "cursor-pointer underline underline-offset-2"
                                                : "cursor-default font-medium text-slate-800 dark:text-slate-100"
                                            }
                                            onClick={() => setSelectedCascadeReason('')}
                                            disabled={!selectedCascadeReason}
                                        >
                                            {isGroupingEnabled ? 'All categories' : 'All reasons'}
                                        </button>
                                        {breadcrumbs.length > 0 && <span className="px-2 text-slate-400">/</span>}
                                    </span>
                                    {breadcrumbs.map((breadcrumbPath, index) => {
                                        const isLastItem = index === breadcrumbs.length - 1;

                                        return (
                                            <span key={breadcrumbPath}>
                                                <button
                                                    type="button"
                                                    className={breadcrumbPath === selectedBreadcrumbPath
                                                        ? "cursor-default font-medium text-slate-800 dark:text-slate-100"
                                                        : "cursor-pointer underline underline-offset-2"
                                                    }
                                                    onClick={() => setSelectedCascadeReason(breadcrumbPath)}
                                                    disabled={breadcrumbPath === selectedBreadcrumbPath}
                                                >
                                                    {isUngroupedDirectSelection
                                                        ? getStopReasonSelectionPath(breadcrumbPath)
                                                        : breadcrumbPath.split('/').pop()}
                                                </button>
                                                {!isLastItem && <span className="px-2 text-slate-400">/</span>}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="ml-auto flex items-center">
                            <Button
                                className="justify-end overflow-hidden text-ellipsis whitespace-nowrap"
                                appearance={isGroupingEnabled ? 'secondary' : 'secondary'}
                                style={{ borderRadius: 0 }}
                                size="small"
                                onClick={() => setIsGroupingEnabled((currentValue) => !currentValue)}
                            >
                                {isGroupingEnabled ? 'Ungroup' : 'Group'}
                            </Button>
                        </div>
                    </div>

                    <ReactECharts
                        ref={chartRef}
                        option={chartOptionsByPath['']}
                        onEvents={chartEvents}
                        style={{ height: chartHeight, width: '100%' }}
                        notMerge={false}
                        lazyUpdate={true}
                        opts={{ renderer: 'canvas' }}
                    />

                    <div ref={triggerContainerRef} className="mt-4 flex min-w-0 flex-1 justify-end sm:min-w-[260px] pl-12 pr-5">
                        <Popover positioning={isMobileView ? "below" : "below-start"}>
                            <PopoverTrigger disableButtonEnhancement>
                                <Button appearance="secondary" className="justify-end overflow-hidden text-ellipsis whitespace-nowrap" style={{ borderRadius: 0 }}>
                                    {categoryButtonLabel}
                                </Button>
                            </PopoverTrigger>
                            <PopoverSurface
                                className="max-w-[calc(100vw-2rem)] overflow-x-hidden p-3"
                                style={{ width: popoverWidth }}
                            >
                                <div className="mb-3 flex flex-wrap gap-2 pb-3 dark:border-gray-700">
                                    <Button appearance="secondary" size="small" onClick={() => setAllReasonsSelected(true)} style={{ borderRadius: 0 }}>
                                        Select all
                                    </Button>
                                    <Button appearance="secondary" size="small" onClick={() => setAllReasonsSelected(false)} style={{ borderRadius: 0 }}>
                                        Hide all
                                    </Button>
                                </div>
                                <div className="flex max-h-72 flex-wrap gap-2 overflow-x-hidden overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:#94a3b8_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-400 dark:[scrollbar-color:#475569_transparent] dark:[&::-webkit-scrollbar-thumb]:bg-slate-600">
                                    {stopReasonData.map((item, index) => {
                                        const backgroundColor = getSecoCategoryColor(index);

                                        return (
                                            <label
                                                key={item.chartPath}
                                                className="box-border flex min-w-0 w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50 md:w-[calc((100%-1rem)/3)] lg:w-[calc((100%-2rem)/5)] dark:hover:bg-slate-800"
                                            >
                                                <Checkbox
                                                    checked={cascadeReasonVisibility[item.visibilityKey] !== false}
                                                    onChange={(_, data) => toggleReason(item.visibilityKey, !!data.checked)}
                                                    label={undefined}
                                                />
                                                <span
                                                    className="h-3 w-3 shrink-0 rounded-full border border-black/10"
                                                    style={item.isOklassificerat
                                                        ? { backgroundImage: `repeating-linear-gradient(135deg, ${SECO_BLUE} 0 4px, ${SECO_BLUE_TINT_20} 4px 8px)` }
                                                        : { backgroundColor }}
                                                />
                                                <span className="min-w-0 break-words text-sm">{isGroupingEnabled ? item.reason : item.fullReason}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </PopoverSurface>
                        </Popover>
                    </div>
                </div>


            </div>
        </div>
    );
}
